import { Octokit } from '@octokit/rest';

import { CatalogApi } from '@backstage/plugin-catalog-react';
import { ConfigApi, OAuthApi, createApiRef } from '@backstage/core-plugin-api';
import { readGitHubIntegrationConfigs } from '@backstage/integration';

import { RepoStatus } from 'plugin-engagement-tracker';
import { EngagementApi } from './types';

export const engagementApiRef = createApiRef<EngagementApi>({
  id: 'plugin.engagement.api',
});

type EngagementClientOptions = {
  catalogApi: CatalogApi;
  configApi: ConfigApi;
  githubAuthApi: OAuthApi;
};

export class EngagementClient implements EngagementApi {
  static fromConfig(options: EngagementClientOptions) {
    return new EngagementClient(
      options.catalogApi,
      options.configApi,
      options.githubAuthApi,
    );
  }

  constructor(
    private readonly catalogApi: CatalogApi,
    private readonly configApi: ConfigApi,
    private readonly githubAuthApi: OAuthApi,
  ) {}

  private async getOctokit(hostname?: string) {
    // TODO: Get access token for the specified hostname
    const token = await this.githubAuthApi.getAccessToken(['repo', 'read:org']);
    const configs = readGitHubIntegrationConfigs(
      this.configApi.getOptionalConfigArray('integrations.github') ?? [],
    );
    const githubIntegrationConfig = configs.find(
      v => v.host === hostname ?? 'github.com',
    );
    const baseUrl = githubIntegrationConfig?.apiBaseUrl;

    return new Octokit({ auth: token, baseUrl });
  }

  private parseRepoFromLocation(location: string): string | undefined {
    const [prefix, ...urlParts] = location.split(':');
    const url = urlParts.join(':');

    if (prefix !== 'url') return undefined;

    const githubPath = new URL(url).pathname.slice(1);
    const [owner, repo] = githubPath.split('/', 2);

    return `${owner}/${repo}`;
  }

  private githubUrlFromRepo(repo: string): string {
    return `https://github.com/${repo}`;
  }

  private async getImportableReposForUser(): Promise<string[]> {
    const octokit = await this.getOctokit();

    const repos = new Set<string>();

    (
      await octokit.paginate(octokit.repos.listForAuthenticatedUser, {
        affiliation: 'owner,collaborator,organization_member',
        per_page: 100,
      })
    )
      .filter(repo => repo.permissions?.push) // Write permission
      .forEach(repo => repos.add(repo.full_name));

    // const orgs = (
    //   await octokit.paginate(octokit.orgs.listMembershipsForAuthenticatedUser, {
    //     state: 'active',
    //     per_page: 100,
    //   })
    // ).map(membership => membership.organization.login);

    // for (const org of orgs) {
    //   (
    //     await octokit.paginate(octokit.repos.listForOrg, {
    //       org,
    //       type: '',
    //       per_page: 100,
    //     })
    //   ).forEach(repo => repos.add(repo.full_name));
    // }

    return Array.from(repos).sort();
  }

  private async getImportedRepos(): Promise<Set<string>> {
    // TODO: Cache on the backend
    const importedRepos = new Set<string>();

    const entities = await this.catalogApi.getEntities({
      filter: { kind: 'Component' },
      fields: ['metadata.name', 'metadata.annotations'],
    });

    for (let entity of entities.items) {
      const annotations = [
        'backstage.io/source-location',
        'backstage.io/managed-by-location', // Can be different
      ];

      for (let annotation of annotations) {
        const location = entity.metadata.annotations?.[annotation];
        if (location) {
          const repo = this.parseRepoFromLocation(location);
          if (repo) {
            importedRepos.add(repo);
          }
        }
      }
    }

    return importedRepos;
  }

  async getReposStatusForUser(): Promise<RepoStatus[]> {
    const userRepos = await this.getImportableReposForUser();
    const importedRepos = await this.getImportedRepos();

    const reposStatus = userRepos.map(repoPath => {
      const [orgName, repoName] = repoPath.split('/', 2);
      return {
        repoName,
        orgName,
        repoPath,
        githubUrl: this.githubUrlFromRepo(repoPath),
        isInCatalog: importedRepos.has(repoPath),
      };
    });

    return reposStatus;
  }
}
